/* Sentinel fence calculator engine: a pure function, inputs + rules + catalogue -> plan.
   No DOM here, so the same file runs in the page and in node for the tests (docs/sentinel-calculator/tests/).
   Rules and their sources: assets/sentinel-calc-data.js. Fix pass 2026-10-17: docs/sentinel-calculator/06-fix-pass.md.
   Every lookup into the rule tables goes through own() so a hostile key (toString, __proto__...) never matches. */
(function (root) {
  'use strict';

  var MOUNT = { bois: 'poteau-bois', t: 'poteau-t', rond: 'poteau-rond' };
  var HOP = Object.prototype.hasOwnProperty;

  function own(o, k) { return o && typeof k === 'string' && HOP.call(o, k) ? o[k] : undefined; }
  function has(list, v) { return Array.isArray(list) && list.indexOf(v) > -1; }
  /* Numbers typed by people: "2,5" and "2.5" are 2.5 (comma decimals, M-7); "2,000" or "2 000" is 2000 (thousands). */
  function num(v, d) {
    if (typeof v === 'string') {
      v = v.replace(/[\s\u00a0\u202f']/g, '').replace(/m$/i, '');
      if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(v)) v = v.replace(/,/g, '');
      else v = v.replace(',', '.');
      if (v === '' || !/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(v)) return d;
    }
    if (typeof v !== 'number' && typeof v !== 'string') return d;
    v = Number(v); return isFinite(v) ? v : d;
  }
  function clampInt(v, lo, hi, d) { v = Math.round(num(v, d)); return Math.max(lo, Math.min(hi, v)); }
  function clampNum(v, lo, hi, d) { v = num(v, d); return Math.max(lo, Math.min(hi, v)); }
  function inStock(p) { return !!p && p.a !== false && (p.s === null || p.s === undefined || p.s > 0); }
  function hiOf(h) { return Array.isArray(h) ? h[1] : h; }
  function midOf(h) { return Array.isArray(h) ? (h[0] + h[1]) / 2 : h; }

  /* Cheapest way to cover `need` units from candidates {p (price, cents), pack (units per item), s (stock or null)}.
     mix = several products may share the line (roll lengths, cable lengths): exhaustive search over the counts of
     every candidate (at most 4), so the result is the true cheapest; ties go to fewer items.
     Without mix: the cheapest single product (one insulator model per fence).
     Returns {picks:[{prod, qty, units}], units, cost, capped}. */
  function cover(cands, need, mix) {
    cands = (cands || []).filter(function (c) { return inStock(c) && c.pack > 0 && c.p > 0; });
    if (!cands.length || !(need > 0)) return null;
    function maxQ(c) { return (c.s === null || c.s === undefined) ? Infinity : c.s; }
    var best = null;
    function consider(qs, cost) {
      var n = 0; for (var i = 0; i < qs.length; i++) n += qs[i];
      if (!best || cost < best.cost || (cost === best.cost && n < best.n)) best = { qs: qs.slice(), cost: cost, n: n };
    }
    if (!mix || cands.length === 1) {
      cands.forEach(function (c, i) {
        var q = Math.ceil(need / c.pack); if (q > maxQ(c)) return;
        var qs = cands.map(function () { return 0; }); qs[i] = q; consider(qs, q * c.p);
      });
    } else {
      var list = cands.slice(0, 4), qs = list.map(function () { return 0; }), work = 1;
      list.forEach(function (c) { work *= Math.min(maxQ(c), Math.ceil(need / c.pack)) + 1; });
      if (work <= 3e6) {
        (function walk(i, left, cost) {
          var c = list[i], top = Math.min(maxQ(c), Math.max(0, Math.ceil(left / c.pack)));
          if (i === list.length - 1) {
            var q = Math.max(0, Math.ceil(left / c.pack));
            if (q <= maxQ(c)) { qs[i] = q; consider(qs, cost + q * c.p); }
            qs[i] = 0; return;
          }
          for (var k = 0; k <= top; k++) {
            if (best && cost + k * c.p > best.cost) break;
            qs[i] = k; walk(i + 1, left - k * c.pack, cost + k * c.p);
          }
          qs[i] = 0;
        })(0, need, 0);
      }
      cands = list;
    }
    if (best) {
      var picks = [], units = 0;
      best.qs.forEach(function (q, i) { if (q > 0) { picks.push({ prod: cands[i], qty: q, units: q * cands[i].pack }); units += q * cands[i].pack; } });
      /* Longest roll first in the list, so "2 x 500 m + 1 x 200 m" reads naturally. */
      picks.sort(function (a, b) { return b.prod.pack - a.prod.pack; });
      return { picks: picks, units: units, cost: best.cost, capped: false };
    }
    /* Not enough stock anywhere: take everything there is, cheapest per unit first. */
    var sorted = cands.slice().sort(function (a, b) { return a.p / a.pack - b.p / b.pack; });
    var out = [], got = 0, total = 0;
    sorted.forEach(function (c) {
      if (got >= need) return;
      var q = Math.min(Math.ceil((need - got) / c.pack), maxQ(c));
      if (q <= 0) return;
      out.push({ prod: c, qty: q, units: q * c.pack }); got += q * c.pack; total += q * c.p;
    });
    return { picks: out, units: got, cost: total, capped: true };
  }

  function rodsFor(spec, R) {
    if (spec && spec.rods) return Math.max(R.rodsMin.v, spec.rods);
    var st = spec && spec.stored_j;
    if (st) { var t = R.rodsByStoredJ.v; for (var i = 0; i < t.length; i++) if (st <= t[i][0]) return Math.max(R.rodsMin.v, t[i][1]); return t[t.length - 1][1]; }
    return R.rodsMin.v;
  }

  /* Wire heights for an animal, a job type and a wire count. Only counts with a published table are "sourced";
     any other count gets evenly spaced working heights (used for the post-height filter and the drawing) and
     sourced:false, so the page says "hauteurs à ajuster sur place" and shows no figure and no source. */
  function profileOf(inp, D) {
    var prof = own(D.animals, inp.animal);
    if (!prof) return null;
    var job = own(D.jobs, inp.job) ? inp.job : 'permanent';
    var temp = !!D.jobs[job].temp;
    var tables = temp ? prof.temp : prof.h;
    var def = temp ? prof.tdef : prof.def;
    var wires = clampInt(inp.wires, 1, 8, def);
    var t = own(tables, String(wires));
    var out = { animal: inp.animal, job: job, temp: temp, wires: wires, def: def, volts: prof.volts, vSrc: prof.vSrc, cond: prof.cond, horse: !!prof.horse, sil: prof.sil || null };
    if (t) {
      out.sourced = true; out.hSrc = t.s; out.min = !!t.min;
      out.range = t.h.map(function (x) { return Array.isArray(x) ? x.slice() : null; });
      out.heights = t.h.map(function (x) { return Math.round(midOf(x)); });
      out.top = Math.max.apply(null, t.h.map(hiOf));
    } else {
      var base = own(tables, String(def)).h.map(midOf), hi = base[base.length - 1], lo = base.length > 1 ? base[0] : Math.round(hi / wires), hs = [];
      if (wires === 1) hs = [Math.round(hi * 2 / 3 + lo / 3)];
      else for (var i = 0; i < wires; i++) hs.push(Math.round(lo + (hi - lo) * i / (wires - 1)));
      out.sourced = false; out.hSrc = null; out.min = false; out.range = hs.map(function () { return null; });
      out.heights = hs; out.top = hs[hs.length - 1];
    }
    return out;
  }

  function plan(input, cat, D) {
    var R = D.rules, inp = Object.assign({}, input || {});
    var job = own(D.jobs, inp.job) ? inp.job : 'permanent';
    inp.job = job;
    var J = D.jobs[job];
    var out = { job: job, lines: [], notes: [], metrics: {}, energizers: {}, assumptions: [] };
    var prof = own(D.animals, inp.animal);
    if (!prof || (job === 'garden') !== (prof.group === 'jardin')) { out.empty = true; return out; }
    var len = Math.round(num(inp.len, 0) * 10) / 10;
    if (!(len > 0)) { out.empty = true; return out; }
    if (len > R.maxLengthM.v) { out.tooLong = true; return out; }
    var swap = inp.swap && typeof inp.swap === 'object' ? inp.swap : {};
    var offs = inp.off && typeof inp.off === 'object' ? inp.off : {};

    var shape = inp.shape === 'line' ? 'line' : 'loop';
    var gates = clampInt(inp.gates, 0, 20, job === 'temporary' ? 0 : 1);
    var corners = clampInt(inp.corners, 0, 40, shape === 'loop' ? 4 : 0);
    var gw = clampNum(inp.gw, 1, 20, R.gateWidthM.v);
    var postType = own(MOUNT, inp.post) || inp.post === 'piquet' ? inp.post : (job === 'temporary' ? 'piquet' : 'bois');
    var P = profileOf(inp, D);
    var wires = P.wires, top = P.top;

    /* Conductor: the buyer's choice, else the profile's; if none is online, the first one that is (horses: tape -> polywire). */
    var condWanted = (inp.cond === 'fil' || inp.cond === 'corde' || inp.cond === 'ruban') ? inp.cond : prof.cond;
    function condOnline(c) { return cat.parts.some(function (p) { return p.role === 'conductor' && has(p.cond, c) && inStock(p); }); }
    var cond = condWanted;
    if (!condOnline(cond)) { cond = ['fil', 'corde', 'ruban'].filter(condOnline)[0] || condWanted; }

    var sp = clampNum(inp.sp, 0, 50, 0) >= 1 ? clampNum(inp.sp, 1, 50, 10) : (cond === 'ruban' ? R.spacingTapeM.v : (wires === 1 ? R.spacingSingleM.v : R.spacingMultiM.v));
    var ends = gates * 2 + (shape === 'line' ? 2 : 0);
    var posts = shape === 'loop' ? Math.ceil(len / sp) : Math.ceil(len / sp) + 1;
    var strainPosts = corners + ends;
    posts = Math.max(posts, strainPosts);
    var linePosts = Math.max(0, posts - strainPosts);
    var exist = J.joinExisting ? clampNum(inp.exist, 0, R.maxLengthM.v, 0) : 0;
    var totalFence = len + exist;
    var needJ = (totalFence / 1000) * R.joulesPerKmFence.v;

    out.profile = Object.assign({}, P, { cond: cond, condWanted: condWanted });
    out.metrics = { len: len, wires: wires, sp: sp, posts: posts, linePosts: linePosts, strainPosts: strainPosts, ends: ends, gates: gates, corners: corners, gw: gw, shape: shape, needJ: needJ, totalFence: totalFence, postType: postType, exist: exist };

    if (cond !== condWanted) out.notes.push({ key: 'tapeMissing', cond: cond });
    if (prof.horse && job === 'temporary') out.notes.push({ key: 'horseTemp' });
    if (inp.road) out.notes.push({ key: 'road' });
    if (inp.winter) out.notes.push({ key: 'winter' });

    var lines = out.lines;
    function partsBy(role, filt) { return cat.parts.filter(function (p) { return p.role === role && (!filt || filt(p)); }); }
    /* Rated for the conductor: the part lists it (product data) and, when its capacity is known, the capacity
       is at least the conductor's size. Rope has no fallback to wire parts (M-1). */
    function fitsCond(p, c) { return has(p.cond, c) && !(p.mm && p.mm < (R.conductorMm.v[c] || 0)); }
    function fitsMount(p) { var m = own(MOUNT, postType); return !!m && has(p.mount, m); }
    function line(o) {
      var all = o.cands || [];
      var pool = all;
      var want = own(swap, o.key);
      if (want !== undefined) { var only = all.filter(function (p) { return String(p.pid) === String(want); }); if (only.length) pool = only; }
      var res = o.need > 0 ? cover(pool, o.need, !!o.mix) : null;
      var L = { key: o.key, group: o.group, need: o.need, reason: o.reason, unitKind: o.unitKind || 'unit', post: !!o.post, optional: !!o.optional,
        off: !!own(offs, o.key), picks: res ? res.picks : [], cost: res ? res.cost : 0, capped: res ? res.capped : false,
        alts: all.filter(inStock), notOnline: !all.length, outOfStock: all.length > 0 && !all.some(inStock), fixed: o.fixed || null,
        rope: cond === 'corde' && !!o.condPart };
      /* Carrier rule (Matt, 2026-10-17, memory reference_fence_carrier_shipping): T-posts, wood posts and round
         posts never ship by carrier. Step-in posts, rods, energizers, wire rolls and every other part ship. */
      L.ship = L.post ? !!o.postShips : true;
      lines.push(L);
      return L;
    }
    function itemOf(p, pack) { return p ? Object.assign({}, p, { pack: pack || p.pack || 1 }) : null; }

    /* ---------------- Energy ---------------- */
    var power = (inp.power === 'secteur' || inp.power === 'batterie' || inp.power === 'solaire' || inp.power === 'existing') ? inp.power : (job === 'temporary' ? 'batterie' : 'secteur');
    if (!J.energizer) power = 'existing';
    var energ = null;
    if (power !== 'existing') {
      var okPow = { secteur: ['secteur', 'mixte'], batterie: ['batterie', 'mixte'], solaire: ['solaire'] }[power];
      var kind = (cat.energizers || []).filter(function (e) { return e && e.spec && has(okPow, e.spec.power); });
      var pool = kind.filter(function (e) { return e.spec.output_j > 0 && inStock(e); }).sort(function (a, b) { return a.p - b.p; });
      var rec = pool.filter(function (e) { return e.spec.output_j >= needJ; })[0] || null;
      /* M-9, Matt 2026-10-17: never jump past a gap in the range. When the need falls between the published-output
         models (the largest one in stock is too weak), and a model in stock WITHOUT a published output sits above that
         one (its STORED joules reach the need: output is always lower than stored, so it may or may not be enough) and
         costs less than half of the first published model that is enough, the tool says so and asks the buyer to write
         instead of recommending the dearer unit. The factor is a store choice (rules.energizerGapFactor). */
      var weak = pool.filter(function (e) { return e.spec.output_j < needJ; });
      var weakTop = weak.length ? Math.max.apply(null, weak.map(function (e) { return e.spec.stored_j || 0; })) : null;
      var unknown = kind.filter(function (e) { return !(e.spec.output_j > 0) && e.spec.stored_j > 0 && e.spec.stored_j >= needJ && inStock(e) && (weakTop === null || e.spec.stored_j > weakTop); })
        .sort(function (a, b) { return a.p - b.p; });
      var gap = weakTop === null ? [] : unknown.filter(function (u) { return !rec || u.p * R.energizerGapFactor.v < rec.p; });
      /* A cheaper model with a published output that would be enough but is out of stock online: named, not sold. */
      var oos = kind.filter(function (e) { return e.spec.output_j >= needJ && !inStock(e) && (!rec || e.p < rec.p); }).sort(function (a, b) { return a.p - b.p; })[0] || null;
      var state = rec ? (gap.length ? 'ask' : 'ok') : (gap.length || unknown.length ? 'ask' : 'none');
      var strong = null, strongWhy = 'double', published = null;
      if (state === 'ok') {
        var up = pool.filter(function (e) { return e !== rec && e.spec.output_j > rec.spec.output_j; });
        var dbl = up.filter(function (e) { return e.spec.output_j >= needJ * R.strongerFactor.v; })[0] || null;
        var next = up.slice().sort(function (a, b) { return a.spec.output_j - b.spec.output_j || a.p - b.p; })[0] || null;
        strong = dbl && dbl.p <= rec.p * 2 ? dbl : next;
        if (strong !== dbl) strongWhy = 'next';
      } else if (state === 'ask' && rec) { published = rec; }
      out.energizers = { state: state, rec: state === 'ok' ? rec : null, strong: strong, strongWhy: strongWhy, published: published,
        unknown: (state === 'ask' ? (rec ? gap : unknown) : []).map(function (u) { return u.spec.model; }), oos: oos ? { model: oos.spec.model, out: oos.spec.output_j } : null,
        needJ: needJ, power: power, pool: pool.length };
      if (state === 'ok') energ = (inp.en === 's' && strong) ? strong : rec;
      else if (state === 'ask' && published && inp.en === 's') energ = published;
      out.energizers.chosen = energ;
      out.energizers.battery = power === 'batterie' && !!energ && (energ.spec.power === 'batterie' || energ.spec.power === 'mixte');
      var EL = line({ key: 'energizer', group: 'energie', need: energ ? 1 : 0, cands: energ ? [itemOf(energ, 1)] : [], reason: 'energizer' });
      EL.energizer = true; EL.notOnline = false;
    } else {
      out.energizers = { state: 'existing', needJ: needJ, power: 'existing' };
      out.notes.push({ key: 'existingCheck' });
    }

    /* ---------------- Grounding ----------------
       Fixed rods (3 x 1,8 m minimum, G-101) for every mains unit, whatever the job (M-4), and for battery or solar units
       above the low-output limit. One portable stake only for a low-output battery or solar unit (G-S20). */
    var groundMode = 'none';
    if (power !== 'existing') {
      if (J.ground === 'fixed' || power === 'secteur') groundMode = 'fixed';
      else {
        var st = energ && energ.spec && energ.spec.stored_j;
        groundMode = (!st || st <= R.lowOutputStoredJ.v) ? 'portable' : 'fixed';
      }
    }
    var rods = 0, lrods = 0;
    var clampCands = partsBy('ground');
    if (groundMode === 'fixed') {
      rods = rodsFor(energ && energ.spec, R);
      line({ key: 'rods', group: 'terre', need: rods, cands: cat.rod ? [itemOf(cat.rod, 1)] : [], reason: 'rods', fixed: rods });
      line({ key: 'clamps', group: 'terre', need: rods * R.clampsPerRod.v, cands: clampCands.map(function (p) { return itemOf(p); }), reason: 'clamps' });
    } else if (groundMode === 'portable') {
      rods = R.rodsPortable.v;
      line({ key: 'rodPortable', group: 'terre', need: rods, cands: cat.rodPortable ? [itemOf(cat.rodPortable, 1)] : [], reason: 'rodPortable' });
    }
    out.metrics.groundMode = groundMode;
    /* Lightning diverter: offered for a mains unit on a permanent fence; OFF unless the buyer adds it (Matt, 2026-10-17),
       shown as a strong recommendation with its cost. Its own rods come with it. */
    var ltAvail = J.lightning === 'default' && power === 'secteur';
    var lightningOn = ltAvail && (inp.lt === undefined || inp.lt === null ? !!cat.lightningDefault : !!Number(inp.lt));
    out.metrics.lightningAvailable = ltAvail;
    out.metrics.lightningOn = lightningOn;
    if (lightningOn) {
      lrods = rods + R.lightningExtraRods.v;
      line({ key: 'lightning', group: 'energie', need: 1, cands: partsBy('surge').map(function (p) { return itemOf(p); }), reason: 'lightning' });
      line({ key: 'lrods', group: 'terre', need: lrods, cands: cat.rod ? [itemOf(cat.rod, 1)] : [], reason: 'lightningRods' });
      line({ key: 'lclamps', group: 'terre', need: lrods, cands: clampCands.map(function (p) { return itemOf(p); }), reason: 'lightningClamps' });
    }

    /* ---------------- Conductor ----------------
       The wire runs the whole length; at each gate it continues across the opening through the gate handle, one
       strand per wire (IA-NRCS: single strand or polytape with an insulated spring-loaded handle). Ties: 1 m per wire
       at every fence end, gate post and handle (assumption, rules.tailPerEndM). */
    var tails = wires * R.tailPerEndM.v * ends + (J.joinExisting ? wires * R.tailPerEndM.v : 0);
    var gateWire = gates * wires * 2 * R.tailPerEndM.v;
    var condNeed = Math.ceil(len * wires + tails + gateWire);
    var longPoly = cond === 'fil' && job !== 'temporary' && len >= R.polywireLongM.v;
    var CL = line({ key: 'conductor', group: 'conducteur', need: condNeed, unitKind: 'roll', reason: 'conductor', mix: true, condPart: true,
      cands: partsBy('conductor', function (p) { return has(p.cond, cond) && p.len > 0; }).map(function (p) { return itemOf(p, p.len); }) });
    /* Long polywire (M-14): past the limit the conductor is held out of the total until the buyer keeps it on purpose. */
    if (longPoly) { out.notes.push({ key: 'longPoly', link: cat.steel || null }); CL.longPoly = true; if (!Number(inp.lpk)) { CL.held = true; CL.off = true; } }
    var rolls = CL.picks.reduce(function (n, k) { return n + k.qty; }, 0);
    out.metrics.condNeed = condNeed; out.metrics.tails = tails; out.metrics.gateWire = gateWire; out.metrics.longPoly = longPoly;

    if (J.reels) line({ key: 'reels', group: 'conducteur', need: wires, condPart: true, cands: partsBy('reel', function (p) { return fitsCond(p, cond); }).map(function (p) { return itemOf(p); }), reason: 'reel' });

    /* ---------------- Insulators ---------------- */
    var stepIn = postType === 'piquet';
    if (!stepIn) {
      line({ key: 'line', group: 'isolateurs', need: linePosts * wires, unitKind: 'pack', reason: 'line', condPart: true,
        cands: partsBy('holder', function (p) { return fitsMount(p) && fitsCond(p, cond); }).map(function (p) { return itemOf(p); }) });
      var strainUnits = (corners + (shape === 'line' ? 2 : 0) + gates * 2) * wires;
      line({ key: 'strain', group: 'isolateurs', need: strainUnits, unitKind: 'pack', reason: 'corner', condPart: true,
        cands: partsBy('gate-anchor', function (p) { return fitsMount(p) && fitsCond(p, cond); })
          .concat(partsBy('end', function (p) { return (!p.mount || !p.mount.length || fitsMount(p)) && fitsCond(p, cond); })).map(function (p) { return itemOf(p); }) });
    }

    /* ---------------- Gates ---------------- */
    if (gates > 0) {
      line({ key: 'handles', group: 'portails', need: gates * wires, reason: 'handle', condPart: true,
        cands: partsBy('handle', function (p) { return fitsCond(p, cond); }).map(function (p) { return itemOf(p); }) });
    }
    if (J.tension && !stepIn) {
      var sections = Math.max(1, Math.ceil(len / R.strainSectionM.v));
      line({ key: 'strainers', group: 'conducteur', need: wires * sections, reason: 'strainer', condPart: true,
        cands: partsBy('strainer', function (p) { return fitsCond(p, cond); }).map(function (p) { return itemOf(p); }) });
    }

    /* ---------------- Connectors and cable ---------------- */
    var crossings = shape === 'loop' ? Math.max(0, gates - 1) : gates;
    var lead = power === 'existing' || groundMode !== 'fixed' ? 0 : clampNum(inp.lead, 1, 500, R.leadOutDefaultM.v);
    var groundRun = groundMode === 'fixed' ? R.groundRunM.v + R.rodSpacingM.v * (rods - 1) : 0;
    var lRun = lightningOn ? R.groundRunM.v + R.rodSpacingM.v * (lrods - 1) : 0;
    var gx = crossings * (gw + R.gateCrossExtraM.v);
    var cableNeed = Math.ceil(lead + groundRun + lRun + gx);
    if (cableNeed > 0 && cat.cable && cat.cable.v && cat.cable.v.length) {
      line({ key: 'cable', group: 'raccords', need: cableNeed, unitKind: 'm', reason: 'cable', mix: true,
        cands: cat.cable.v.filter(function (v) { return v.len > 0; }).map(function (v) { return Object.assign({}, cat.cable, v, { pack: v.len, v: null }); }) });
    } else if (cableNeed > 0) {
      line({ key: 'cable', group: 'raccords', need: cableNeed, unitKind: 'm', reason: 'cable', cands: [] });
    }
    out.metrics.cable = { lead: lead, ground: groundRun, lightning: lRun, gates: gx, need: cableNeed, crossings: crossings };

    var points = 2 + Math.floor(Math.max(0, len - 1) / R.joinEveryM.v);
    var links = (wires - 1) * points;
    var joins = Math.max(0, rolls - wires);
    var cableEnds = (lead > 0 ? 1 : 0) + crossings * 2 + (lightningOn ? 1 : 0);
    var joinExisting = J.joinExisting ? wires : 0;
    var connNeed = links + joins + cableEnds + joinExisting;
    out.metrics.connectors = { links: links, joins: joins, cables: cableEnds, existing: joinExisting };
    if (connNeed > 0 && !(J.reels && wires === 1 && cableEnds === 0 && joins === 0)) {
      line({ key: 'connectors', group: 'raccords', need: connNeed, reason: J.joinExisting ? 'connectorsExtend' : 'connectors', condPart: true,
        cands: partsBy('connector', function (p) { return fitsCond(p, cond); }).map(function (p) { return itemOf(p); }) });
    }

    /* ---------------- Posts (optional) ---------------- */
    var wantPosts = inp.addPosts === undefined || inp.addPosts === null ? J.posts : !!inp.addPosts;
    out.metrics.addPosts = wantPosts;
    if (wantPosts) {
      if (postType === 't') {
        line({ key: 'posts', group: 'poteaux', need: posts, post: true, optional: true, reason: 'postsT',
          cands: (cat.posts || []).filter(function (p) { return p.kind === 't'; }).map(function (p) { return itemOf(p, 1); }) });
      } else if (stepIn) {
        line({ key: 'posts', group: 'poteaux', need: posts, post: true, postShips: true, optional: true, reason: 'postsStep',
          cands: (cat.posts || []).filter(function (p) { return p.kind === 'step' && (!p.h || p.h >= top + R.postOverTopWireCm.v); }).map(function (p) { return itemOf(p, 1); }) });
      } else {
        line({ key: 'posts', group: 'poteaux', need: posts, post: true, optional: true, reason: postType === 'bois' ? 'postsWood' : 'postsRound', cands: [] });
      }
      if (postType !== 'piquet') out.notes.push({ key: 'braces' });
    }

    /* ---------------- Testing ---------------- */
    var testerOn = inp.tester === undefined || inp.tester === null ? J.tester : !!Number(inp.tester);
    if (testerOn) line({ key: 'tester', group: 'verif', need: 1, reason: 'tester', cands: cat.tester ? [itemOf(cat.tester, 1)] : [] });
    out.metrics.testerOn = testerOn;

    /* ---------------- Stock per variant across lines (m-1) ----------------
       Rods and lightning rods (and clamps) share a variant: the sum may not exceed the online stock. */
    var used = {};
    lines.forEach(function (L) {
      L.picks.forEach(function (k) {
        var s = k.prod.s; if (s === null || s === undefined) return;
        var id = k.prod.id, before = used[id] || 0, room = Math.max(0, s - before);
        if (k.qty > room) { k.qty = room; k.units = room * k.prod.pack; L.capped = true; }
        used[id] = before + k.qty;
      });
      L.picks = L.picks.filter(function (k) { return k.qty > 0; });
      L.cost = L.picks.reduce(function (a, k) { return a + k.qty * k.prod.p; }, 0);
    });

    /* Rope (6 mm): roles with no rope-rated part online. */
    out.metrics.ropeSoon = lines.filter(function (L) { return L.rope && !L.picks.length; }).map(function (L) { return L.key; });

    return out;
  }

  /* Totals for a shipping mode: 'ship' leaves out every line that cannot go by carrier. Held or removed lines never count. */
  function totals(res, mode) {
    var t = { all: 0, kept: 0, left: [], items: [], grams: 0, weightsComplete: true, missingWeights: [] };
    (res.lines || []).forEach(function (L) {
      if (L.off || !L.picks.length) return;
      var skip = mode === 'ship' && !L.ship;
      L.picks.forEach(function (k) {
        var cost = k.qty * k.prod.p;
        t.all += cost;
        if (skip) return;
        t.kept += cost;
        t.items.push({ id: k.prod.id, quantity: k.qty, key: L.key, pid: k.prod.pid, ship: L.ship });
        if (L.ship) {
          if (k.prod.g > 0) t.grams += k.prod.g * k.qty;
          else { t.weightsComplete = false; if (t.missingWeights.indexOf(k.prod.t) < 0) t.missingWeights.push(k.prod.t); }
        }
      });
      if (skip) t.left.push(L.key);
    });
    /* One cart line per variant (m-8): rods and lightning rods merge in the cart, so they merge here. */
    var by = {}, merged = [];
    t.items.forEach(function (i) { if (by[i.id]) by[i.id].quantity += i.quantity; else { by[i.id] = Object.assign({}, i); merged.push(by[i.id]); } });
    t.cartItems = merged;
    return t;
  }

  /* Catalogue preparation (round 5, additive: plan() and totals() are unchanged). Parmak energizers that publish only
     unqualified joules get the documented, cautious output estimate from rules.parmakModels / parmakOutputRatio, so
     they compete on the same output-joule rule as Gallagher. Their published figure is used as stored joules for the
     ground rule. Never applied to a unit that already has a published output, nor to a model not in the table. */
  function prepareCatalog(cat, D) {
    var R = D.rules, M = R.parmakModels && R.parmakModels.v, ratio = R.parmakOutputRatio && R.parmakOutputRatio.v;
    if (!cat || !M || !ratio) return cat;
    (cat.energizers || []).forEach(function (e) {
      var m = e && own(M, String(e.pid));
      if (m && R.parmakModels.brand) e.b = R.parmakModels.brand; /* a listed model is that brand, whatever a translated label says */
      if (!m || !e.spec || e.spec.output_j > 0) return;
      var out = Math.round(Math.min(m.high * ratio, m.low) * 100) / 100;
      e.spec = Object.assign({}, e.spec, { output_j: out, stored_j: m.high, power: m.power, estimated: true, est: { high: m.high, low: m.low, ratio: ratio, miles: m.miles, u: m.u } });
    });
    return cat;
  }

  var API = { plan: plan, totals: totals, cover: cover, profileOf: profileOf, own: own, num: num, prepareCatalog: prepareCatalog };
  root.SentinelCalcEngine = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
